import React from 'react';
import {Dialog, DialogActions, DialogContent, DialogTitle} from "@mui/material";
import {useTranslation} from "react-i18next";
import {ActionButton, CancelButton} from "../../common/ActionButtons";
import {
    EmptyState,
    EmptyStateText,
    PanelContainer,
    PanelHeader,
    ScrollableContent,
    SectionDivider
} from "./FileSelectionDialog.styled";
import DataSourceMultiSelect from "../DataSourceMultiSelect";
import FileSelectionItemsGrid from "../FileSelectionItemsGrid";
import {useChatDataSourceDocuments} from "../useChatDataSourceDocuments";

interface FileSelectionDialogProps {
    open: boolean;
    onClose: (isCancelled: boolean) => void;
}

const FileSelectionDialog = ({open, onClose}: FileSelectionDialogProps) => {
    const {t} = useTranslation();
    const {
        isLoadingDataSources,
        dataSources,
        itemsByGroup,
        loadingGroupIds,
        sortedSelectedDataSources,
        selectedGroupIds,
        pageSizeOptions,
        getGroupPagination,
        handleLoadGroupItems,
        handleDataSourceSelect,
        handleConfirmDocumentsSelection,
        handlePageChange,
        handlePageSizeChange,
        handleSearchChange,
    } = useChatDataSourceDocuments();

    const hasAnySelectedSources = sortedSelectedDataSources.length > 0;

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
            <DialogTitle>{t('attachFiles.dialogTitle')}</DialogTitle>

            <DialogContent>
                <PanelContainer $isOpen={open}>
                    <PanelHeader>
                        <DataSourceMultiSelect
                            dataSources={dataSources}
                            selectedIds={selectedGroupIds}
                            onChange={handleDataSourceSelect}
                        />
                    </PanelHeader>

                    <ScrollableContent $isEmpty={!hasAnySelectedSources}>
                        {!isLoadingDataSources && !hasAnySelectedSources && (
                            <EmptyState>
                                <EmptyStateText>
                                    {t('fileSelection.noSourcesSelected', {defaultValue: 'Wybierz źródła danych aby zobaczyć pliki'})}
                                </EmptyStateText>
                            </EmptyState>
                        )}

                        {sortedSelectedDataSources.map((source, index) => {
                            return (
                                <React.Fragment key={`${source.id}-${source.name}-${index}`}>
                                    {index > 0 && <SectionDivider/>}

                                    <FileSelectionItemsGrid
                                        source={source}
                                        itemsByGroup={itemsByGroup}
                                        hideSelectAllItems
                                        getGroupPagination={getGroupPagination}
                                        isLoadingGroupItems={loadingGroupIds.includes(source.id)}
                                        pageSizeOptions={pageSizeOptions}
                                        onLoadGroupItems={handleLoadGroupItems}
                                        onPageSizeChange={handlePageSizeChange}
                                        onCurrentPageChange={handlePageChange}
                                        onSearchChange={handleSearchChange}
                                    />
                                </React.Fragment>
                            );
                        })}
                    </ScrollableContent>
                </PanelContainer>
            </DialogContent>

            <DialogActions sx={{ pt: 2 }}>
                <CancelButton onClick={() => onClose(true)}>
                    {t('common.cancel')}
                </CancelButton>

                <ActionButton onClick={() => handleConfirmDocumentsSelection(onClose)}>
                    {t('common.confirm')}
                </ActionButton>
            </DialogActions>
        </Dialog>
    );
};

export default FileSelectionDialog;